import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Dropdown, {
  divider,
} from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { emptyArr, areArraysEqual } from 'util/arrays'
import usePrevious from 'util/hooks/usePrevious'
import { DRAWER_TYPE_TAGS_UPDATE } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { doFetchTagsV2By } from 'ducks/tags/actions'
import { selectCurrentTagsById } from 'ducks/tags/selectors'
import TagDropdownCreateButton from '../TagDropdown/createButton'
import Option from './Option'
import useSearchTags from './useSearchTags'
import { Footer, styles, useMultiSelect } from '../MultiSelectMenu'
import MenuWithSearch, {
  useShowHeaderShadow,
  styles as menuWithSearchStyles,
} from '../MenuWithSearch'

const TagsDropdown = ({
  fieldType = 'ids',
  value: inputValue,
  setValue,
  selectedIds,
  onSelectedIdsChange,
}) => {
  const searchRef = useRef()
  const dispatch = useDispatch()
  const value = inputValue || emptyArr
  const [menuVisible, setMenuVisible] = useState(false)
  const { handleScroll, shouldShowHeaderShadow } = useShowHeaderShadow(
    menuVisible
  )
  const currentTagsById = useSelector(selectCurrentTagsById)
  const { search, handleChangeSearch, filteredTags } = useSearchTags({
    searchDelayMs: 200,
    field: fieldType,
  })
  const { handleChange } = useMultiSelect(
    filteredTags,
    onSelectedIdsChange,
    selectedIds
  )
  const { openDrawer: openEditDrawer } = useDrawer(DRAWER_TYPE_TAGS_UPDATE)
  const handleOnCreateTag = useCallback(
    () => {
      openEditDrawer('new')
    },
    [openEditDrawer]
  )

  const handleMenuVisibleChange = useCallback(visible => {
    setMenuVisible(visible)
  }, [])
  const handleSelect = useCallback(
    () => {
      setValue(selectedIds)
      setMenuVisible(false)
    },
    [setValue, selectedIds]
  )
  const handleCancel = useCallback(() => {
    setMenuVisible(false)
  }, [])

  const menu = useMemo(
    () => {
      return (
        <MenuWithSearch
          focusElementRef={searchRef}
          search={search}
          onScroll={handleScroll}
        >
          <TagDropdownCreateButton
            onCreateTag={handleOnCreateTag}
            className="grui px-10 pb-5"
          />
          {divider}
          <div className="grui pt-3 pb-5">
            {filteredTags.map(({ id, name, conversationCount, color }) => (
              <MenuWithSearch.Item itemKey={id} key={id}>
                <Option
                  css={styles.checkbox}
                  id={id}
                  onChange={handleChange}
                  checked={selectedIds.some(itemId => itemId === id)}
                  name={name}
                  conversationCount={conversationCount}
                  color={color}
                />
              </MenuWithSearch.Item>
            ))}
          </div>
        </MenuWithSearch>
      )
    },
    [
      filteredTags,
      selectedIds,
      handleChange,
      handleOnCreateTag,
      handleScroll,
      search,
    ]
  )
  const header = useMemo(
    () => (
      <MenuWithSearch.Search
        placeholder="Search all tags"
        onChange={handleChangeSearch}
        value={search}
        ref={searchRef}
        shouldFocus={menuVisible}
      />
    ),
    [handleChangeSearch, search, menuVisible]
  )
  const footer = useMemo(
    () => (
      <Footer
        onSelect={handleSelect}
        onCancel={handleCancel}
        saveBtnTitle="Select tags"
      />
    ),
    [handleSelect, handleCancel]
  )

  const selectedNames = useMemo(
    () => {
      if (fieldType === 'ids') {
        return value
          .map(id => currentTagsById[id]?.name)
          .filter(name => !!name)
          .join(', ')
      }

      return value.filter(name => !!name).join(', ')
    },
    [fieldType, value, currentTagsById]
  )

  useEffect(
    () => {
      if (!menuVisible) {
        onSelectedIdsChange(value)
        handleChangeSearch({ target: { value: '' } })
      }
    },
    // Including onSelectedIdsChange causes circular renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuVisible, value]
  )

  const previousValue = usePrevious(value)
  useEffect(
    () => {
      const tagIdsToFetch = value.filter(tagId => !currentTagsById[tagId])
      const filterValue = fieldType === 'ids' ? tagIdsToFetch : value
      if (
        filterValue &&
        filterValue.length &&
        !areArraysEqual(filterValue || [], previousValue || [])
      ) {
        dispatch(
          doFetchTagsV2By(fieldType, {
            filter: filterValue,
          })
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldType, value, previousValue]
  )

  return (
    <Dropdown
      overlay={menu}
      header={header}
      footer={footer}
      css={[
        text.styles.textSm,
        styles.dropdownContainer,
        shouldShowHeaderShadow && menuWithSearchStyles.showHeaderShadow,
      ]}
      visible={menuVisible}
      onVisibleChange={handleMenuVisibleChange}
      emptyHint=""
      autoHeight
    >
      <Dropdown.Button css={styles.dropdownBtn} size="small">
        {value.length ? selectedNames : `Select tags`}
      </Dropdown.Button>
    </Dropdown>
  )
}

export default TagsDropdown
